import { memo, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';

import { Modal } from '@unified-checkout/design-system';

import type { IQRCodeModalProps } from '@app/dashboard/types/shared/modals/qrcode-modal';
import { useLangStore } from '@app/dashboard/store/lang';

import { PaymobLogo } from '@unified-checkout/design-system/icons/paymob_logo';

import styles from './qr-code-modal.module.scss';

const DEFAULT_LOCALIZATION = {
  en: {
    title: 'Scan QR Code',
    footer: 'Scan and pay',
  },
  ar: {
    title: 'مسح رمز الاستجابة السريعة',
    footer: 'امسح و ادفع',
  },
};

const QRCardCenterLogo = ({ src }: { src: string }) => {
  const [hide, setHide] = useState(false);

  if (hide) return null;

  return (
    <div className={styles['qr-code-modal__body-card-qr-code-logo']}>
      <img
        src={src}
        alt="Business logo"
        className={styles['qr-code-modal__body-card-qr-code-logo-img']}
        crossOrigin="anonymous"
        onError={() => setHide(true)}
      />
    </div>
  );
};

const QRCodeModal = ({
  modalBodyRef,
  modalProps: {
    isModalOpen,
    handleCloseModal,
    hasCloseIcon = true,
    closeOnClickOutside = false,
    title,
    isOverlayAboveModal = true,
    subtitle,
    width,
    footerBtn,
  },
  cardProps: {
    showPaymobTheme = true,
    cardTitle,
    cardSubTitle,
    cardFooter,
    showFooter = true,
    cardLogo,
    QRCodeValue = '',
  } = {},
}: IQRCodeModalProps) => {
  const { lang } = useLangStore();

  const hasTitleOrSubtitle = !!cardTitle || !!cardSubTitle;
  let modalWidth = width || (showPaymobTheme ? '35.3rem' : '32rem');

  return (
    <Modal
      open={isModalOpen}
      onCloseHandler={handleCloseModal}
      hasCloseIcon={hasCloseIcon}
      closeOnClickOutside={closeOnClickOutside}
      width={modalWidth}
      isOverlayAboveModal={isOverlayAboveModal}
      title={title || DEFAULT_LOCALIZATION[lang].title}
      supportingText={subtitle}
      btnsProps={footerBtn}
      wrapperClassName={styles['qr-code-modal']}
      position="center"
    >
      <div
        className={styles['qr-code-modal__body']}
        data-modal-body-paymob-theme={showPaymobTheme}
        ref={modalBodyRef}
      >
        <div className={styles['qr-code-modal__body-card']}>
          {showPaymobTheme && (
            <div className={styles['qr-code-modal__body-card-logo']}>
              {PaymobLogo}
            </div>
          )}
          {hasTitleOrSubtitle && (
            <div className={styles['qr-code-modal__body-card-header']}>
              {cardTitle && (
                <p className={styles['qr-code-modal__body-card-header-title']}>
                  {cardTitle}
                </p>
              )}
              {cardSubTitle && (
                <p
                  className={styles['qr-code-modal__body-card-header-subtitle']}
                >
                  {cardSubTitle}
                </p>
              )}
            </div>
          )}
          {QRCodeValue && (
            <div className={styles['qr-code-modal__body-card-qr-code']}>
              <QRCodeSVG
                value={QRCodeValue}
                width="12rem"
                height="12rem"
                level="H"
              />
              {cardLogo && (
                <QRCardCenterLogo key={cardLogo} src={cardLogo} />
              )}
            </div>
          )}
          {showFooter && (
            <p className={styles['qr-code-modal__body-card-footer']}>
              {cardFooter || DEFAULT_LOCALIZATION[lang].footer}
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default memo(QRCodeModal);
